import type { PersonalOffice } from "@/hooks/use-personal-offices";
import type { PersonalOfficeSnapshot } from "@/lib/office/protocol";
import { type FeedItem, initialsOf, type SideTab, STATUSES, UNLOCKS } from "./constants";
import type { VyllagePerson } from "./world-engine";

type SidePanelProps = {
  tab: SideTab;
  onTabChange: (tab: SideTab) => void;
  feed: FeedItem[];
  feedElRef: React.RefObject<HTMLDivElement | null>;
  chatInputRef: React.RefObject<HTMLInputElement | null>;
  onSendChat: () => void;
  people: VyllagePerson[];
  onWalkTo: (id: string, firstName: string) => void;
  onWalkToOffice: (slotKey: string, label: string) => void;
  points: number;
  streak: number;
  unlockedIds: string[];
  nextUnlockLabel: string;
  progressPct: string;
  officeByOwnerId?: Map<string, PersonalOfficeSnapshot>;
  /** All personal office snapshots (claimed + empty slots with names). */
  offices?: PersonalOfficeSnapshot[];
  myOffice?: PersonalOffice | null;
};

export function SidePanel({
  tab,
  onTabChange,
  feed,
  feedElRef,
  chatInputRef,
  onSendChat,
  people,
  onWalkTo,
  onWalkToOffice,
  points,
  streak,
  unlockedIds,
  nextUnlockLabel,
  progressPct,
  officeByOwnerId,
  offices = [],
  myOffice,
}: SidePanelProps) {
  const claimedOffices = offices
    .filter((o) => o.ownerUserId)
    .slice()
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  return (
    <div className="flex h-full min-h-0 w-full shrink-0 flex-col bg-[var(--vy-surface)]">
      <div className="flex shrink-0 border-b border-[var(--vy-surface-border)]">
        {(
          [
            ["chat", "Chat"],
            ["people", "People"],
            ["perks", "Perks"],
          ] as const
        ).map(([id, label]) => {
          const active = tab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onTabChange(id)}
              className="flex-1 cursor-pointer border-0 border-b-2 bg-transparent px-0 pb-2.5 pt-3 text-[12.5px] font-bold tracking-wide"
              style={{
                fontFamily: "var(--font-body)",
                borderBottomColor: active ? "var(--vy-accent)" : "transparent",
                color: active ? "var(--vy-surface-fg)" : "var(--vy-surface-fg-3)",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {tab === "chat" && (
        <div className="flex min-h-0 flex-1 flex-col">
          <div ref={feedElRef} className="flex flex-1 flex-col gap-2.5 overflow-y-auto p-3">
            <div className="self-center rounded-full bg-[var(--vy-surface-hover)] px-3 py-1 text-[10.5px] font-semibold tracking-wide text-[var(--vy-surface-fg-3)]">
              LIVE SPATIAL CHAT — NEARBY PLAYERS ONLY
            </div>
            {feed.map((m) =>
              m.kind === "sys" ? (
                <div
                  key={m.id}
                  className="self-center rounded-full bg-[var(--vy-surface-accent)] px-3 py-1 text-[10.5px] font-semibold text-[var(--vy-accent)]"
                >
                  {m.text}
                </div>
              ) : (
                <div
                  key={m.id}
                  className="flex gap-2"
                  style={{ opacity: String(0.45 + 0.55 * Math.min(1, m.vol)) }}
                >
                  <div
                    className="mt-0.5 flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-[9.5px] font-bold text-white"
                    style={{ background: m.color }}
                  >
                    {initialsOf(m.from)}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xs font-bold text-[var(--vy-surface-fg)]">
                        {m.from}
                      </span>
                      <span className="text-[10px] text-[var(--vy-surface-fg-3)]">{m.zone}</span>
                    </div>
                    <div className="mt-px text-[12.5px] leading-snug text-[var(--vy-surface-fg-2)]">
                      {m.text}
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>
          <div className="flex shrink-0 gap-2 border-t border-[var(--vy-surface-border)] p-2.5">
            <input
              ref={chatInputRef}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSendChat();
              }}
              placeholder="Say something nearby…"
              className="min-w-0 flex-1 rounded-full border border-[var(--vy-surface-border)] bg-[var(--vy-surface)] px-3.5 py-2 text-[13px] text-[var(--vy-surface-fg)] outline-none"
              style={{ fontFamily: "var(--font-body)" }}
            />
            <button
              type="button"
              onClick={onSendChat}
              className="shrink-0 cursor-pointer rounded-full bg-[var(--vy-bar)] px-4 py-2 text-[12.5px] font-bold text-white hover:bg-[var(--vy-bar-deep)]"
              style={{ fontFamily: "var(--font-body)" }}
            >
              Send
            </button>
          </div>
        </div>
      )}

      {tab === "people" && (
        <div className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2.5">
          {myOffice ? (
            <div className="mb-2 flex items-center gap-2 rounded-[10px] border border-[var(--vy-surface-border)] bg-[var(--vy-surface-hover)] px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold tracking-wide text-[var(--vy-surface-fg-3)]">
                  YOUR OFFICE
                </div>
                <div className="truncate text-[12.5px] font-bold text-[var(--vy-surface-fg)]">
                  {myOffice.displayName}
                </div>
                <div className="text-[10.5px] text-[var(--vy-surface-fg-3)]">
                  Door {myOffice.doorStatus === "open" ? "open to guests" : "closed"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onWalkToOffice(myOffice.slotKey, myOffice.displayName)}
                className="shrink-0 cursor-pointer rounded-full border border-[var(--vy-surface-border)] bg-[var(--vy-surface)] px-3 py-1 text-[11px] font-bold text-[var(--vy-surface-fg)] hover:border-[var(--vy-bar)] hover:bg-[var(--vy-bar)] hover:text-white"
                style={{ fontFamily: "var(--font-body)" }}
              >
                Go to office
              </button>
            </div>
          ) : null}

          <div className="mb-1 px-1 text-[10px] font-bold tracking-wide text-[var(--vy-surface-fg-3)]">
            PEOPLE IN HQ
          </div>
          {people.map((pr) => {
            const statusColor = STATUSES.find((s) => s.id === pr.status)?.color ?? "var(--vy-good)";
            const first = pr.name.split(" ")[0] ?? pr.name;
            const owned = officeByOwnerId?.get(pr.id);
            return (
              <div
                key={pr.id}
                className="flex items-center gap-2.5 rounded-[10px] p-2 hover:bg-[var(--vy-surface-hover)]"
              >
                <div
                  className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
                  style={{ background: pr.color }}
                >
                  {initialsOf(pr.name)}
                  <span
                    className="absolute -bottom-px -right-px h-2.5 w-2.5 rounded-full border-2 border-white"
                    style={{ background: statusColor }}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12.5px] font-bold text-[var(--vy-surface-fg)]">
                    {pr.name}
                  </div>
                  <div className="truncate text-[10.5px] text-[var(--vy-surface-fg-3)]">
                    {pr.role} · {pr.zone}
                  </div>
                  {owned ? (
                    <div className="truncate text-[10px] font-semibold text-[var(--vy-accent)]">
                      {owned.displayName} · {owned.doorStatus === "open" ? "Open" : "Closed"}
                    </div>
                  ) : null}
                  <div className="mt-1 h-[3px] w-[90px] overflow-hidden rounded-sm bg-[var(--vy-surface-hover)]">
                    <div
                      className="h-full rounded-sm bg-[var(--vy-accent)]"
                      style={{ width: `${Math.round(pr.vol * 100)}%` }}
                    />
                  </div>
                </div>
                <div className="flex shrink-0 flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => onWalkTo(pr.id, first)}
                    className="cursor-pointer rounded-full border border-[var(--vy-surface-border)] bg-[var(--vy-surface)] px-3 py-1 text-[11px] font-bold text-[var(--vy-surface-fg)] hover:border-[var(--vy-bar)] hover:bg-[var(--vy-bar)] hover:text-white"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    Go
                  </button>
                  {owned ? (
                    <button
                      type="button"
                      onClick={() => onWalkToOffice(owned.slotKey, owned.displayName)}
                      className="cursor-pointer rounded-full border border-[var(--vy-surface-border)] bg-[var(--vy-surface)] px-3 py-1 text-[11px] font-bold text-[var(--vy-surface-fg)] hover:border-[var(--vy-accent)] hover:bg-[var(--vy-accent)] hover:text-white"
                      style={{ fontFamily: "var(--font-body)" }}
                      title={
                        owned.doorStatus === "closed"
                          ? "Walk to their door (office is closed)"
                          : "Walk to their office"
                      }
                    >
                      Office
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}

          {claimedOffices.length > 0 ? (
            <>
              <div className="mb-1 mt-3 px-1 text-[10px] font-bold tracking-wide text-[var(--vy-surface-fg-3)]">
                OFFICES
              </div>
              {claimedOffices.map((o) => (
                <div
                  key={o.slotKey}
                  className="flex items-center gap-2.5 rounded-[10px] p-2 hover:bg-[var(--vy-surface-hover)]"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--vy-bar)] text-[10px] font-bold text-white">
                    {o.slotKey.replace("office_", "#")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12.5px] font-bold text-[var(--vy-surface-fg)]">
                      {o.displayName}
                    </div>
                    <div className="truncate text-[10.5px] text-[var(--vy-surface-fg-3)]">
                      {o.doorStatus === "open" ? "Open to guests" : "Closed · private"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onWalkToOffice(o.slotKey, o.displayName)}
                    className="shrink-0 cursor-pointer rounded-full border border-[var(--vy-surface-border)] bg-[var(--vy-surface)] px-3 py-1 text-[11px] font-bold text-[var(--vy-surface-fg)] hover:border-[var(--vy-bar)] hover:bg-[var(--vy-bar)] hover:text-white"
                    style={{ fontFamily: "var(--font-body)" }}
                  >
                    Go to office
                  </button>
                </div>
              ))}
            </>
          ) : null}
        </div>
      )}

      {tab === "perks" && (
        <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-3.5">
          <div className="rounded-xl bg-[var(--vy-bar)] px-4 py-3.5">
            <div className="text-[10px] font-bold tracking-[0.14em] text-white/55">
              VYLLAGE POINTS
            </div>
            <div
              className="text-[32px] font-bold leading-tight text-white"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {points}
            </div>
            <div className="text-[11.5px] font-semibold text-[var(--vy-accent)]">
              {streak}-day check-in streak
            </div>
          </div>
          <div>
            <div className="mb-1 flex justify-between text-[11px] text-[var(--vy-surface-fg-3)]">
              <span className="font-bold text-[var(--vy-surface-fg-2)]">Next unlock</span>
              <span>{nextUnlockLabel}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-sm bg-[var(--vy-surface-hover)]">
              <div
                className="h-full rounded-sm"
                style={{ width: progressPct, background: "var(--nymbl-gradient)" }}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {UNLOCKS.map((u) => {
              const unlocked = unlockedIds.includes(u.id) || points >= u.pts;
              return (
                <div
                  key={u.id}
                  className="flex items-center gap-2.5 rounded-[10px] border border-[var(--vy-surface-border)] px-2.5 py-2"
                  style={{ opacity: unlocked ? 1 : 0.55 }}
                >
                  <div
                    className="h-[30px] w-[30px] shrink-0 rounded-lg"
                    style={{ background: u.swatch }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-bold text-[var(--vy-surface-fg)]">
                      {u.name}
                    </div>
                    <div className="text-[10.5px] text-[var(--vy-surface-fg-3)]">{u.desc}</div>
                  </div>
                  <span
                    className="shrink-0 text-[10.5px] font-bold"
                    style={{ color: unlocked ? "var(--vy-good)" : "var(--vy-surface-fg-3)" }}
                  >
                    {unlocked ? "UNLOCKED" : `${u.pts} PTS`}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="text-[11px] leading-relaxed text-[var(--vy-surface-fg-3)]">
            Earn points by showing up, waving, chatting nearby, and joining meetings. Unlocked decor
            appears in the office.
          </div>
        </div>
      )}
    </div>
  );
}
